/**
 * SubmittedResumeView — readable, on-screen rendering of an IMMUTABLE submitted resume
 * snapshot (used inside the Application Workspace). Presentation only; renders exactly the
 * snapshot data passed in. Mirrors the candidate resume preview so the hiring manager can
 * read the candidate without opening a tiny embedded viewer or a PDF.
 */
import { MapPin, Mail, Phone } from 'lucide-react';
import { sanitizeMosTitle } from '@/utils/militaryTranslation';
import { AwardsDisplay } from '@/components/AwardsDisplay';

function parseBullets(text) {
  if (!text) return [];
  const lower = text.toLowerCase();
  if (lower.includes('<!doctype') || lower.includes('<html') || lower.includes('ratelimiterror') ||
      lower.includes('rate_limit_exceeded') || (text.match(/<[a-zA-Z/][^>]*>/g) || []).length > 3) {
    return [];
  }
  return text.split('\n').map((l) => l.trim()).filter((l) => l && !l.startsWith('#')).map((l) => l.replace(/^[•\-*▪◦●]\s*/, ''));
}

export function SubmittedResumeView({ resume }) {
  if (!resume) return null;
  const pi = resume.personal_info || {};

  const coreCompetencies = (() => {
    if (!resume.military_roles?.length) return [];
    const all = [];
    for (const role of resume.military_roles) {
      for (const key of ['selected_abilities', 'selected_skills', 'selected_knowledge']) {
        for (const item of (role[key] || [])) {
          const n = typeof item === 'object' ? item.element_name : item;
          if (n && !all.includes(n)) all.push(n);
        }
      }
    }
    const seen = new Set();
    return all.filter((c) => { const l = c.toLowerCase().trim(); if (seen.has(l)) return false; seen.add(l); return true; }).slice(0, 15);
  })();

  return (
    <div data-testid="submitted-resume-view">
      <div className="border-b border-slate-200 pb-4 mb-5">
        <h3 className="text-2xl font-bold text-slate-900">{pi.full_name || 'Candidate'}</h3>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-slate-600">
          {pi.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 flex-shrink-0" /> {pi.city}{pi.state ? `, ${pi.state}` : ''}</span>}
          {pi.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4 flex-shrink-0" /> {pi.email}</span>}
          {pi.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4 flex-shrink-0" /> {pi.phone}</span>}
        </div>
      </div>

      {resume.professional_summary && (
        <section className="mb-6">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Professional Summary</h4>
          <p className="text-[15px] text-slate-700 leading-relaxed">{resume.professional_summary}</p>
        </section>
      )}

      {coreCompetencies.length > 0 && (
        <section className="mb-6">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Core Competencies</h4>
          <p className="text-[15px] text-slate-700 leading-relaxed">{coreCompetencies.join(' \u2022 ')}</p>
        </section>
      )}

      {resume.military_roles?.length > 0 && (
        <section className="mb-6">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Military Experience</h4>
          {resume.military_roles.map((role, idx) => {
            const bullets = parseBullets(role.ai_summary_text);
            return (
              <div key={idx} className="mb-4 pl-3 border-l-2 border-brand">
                <p className="font-semibold text-slate-900">{role.rank}, {role.branch}</p>
                <p className="text-sm text-slate-600">{role.mos_code} - {sanitizeMosTitle(role.mos_title, role.rank)}</p>
                <p className="text-xs text-slate-400 font-mono">{role.location} | {role.start_month}/{role.start_year} - {role.end_month}/{role.end_year}</p>
                {bullets.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-[15px] text-slate-700"><span className="text-slate-400 mt-0.5">&bull;</span><span>{b}</span></li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </section>
      )}

      {resume.civilian_experience?.some((e) => (e.job_title || '').trim() || (e.company || '').trim() || (e.description || '').trim()) && (
        <section className="mb-6">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Professional Experience</h4>
          {resume.civilian_experience.map((exp, idx) => (
            <div key={idx} className="mb-4">
              <p className="font-semibold text-slate-900">{exp.job_title}</p>
              <p className="text-sm text-slate-600">{exp.company}{exp.location ? ` - ${exp.location}` : ''}</p>
              <p className="text-xs text-slate-400 font-mono">{exp.start_month}/{exp.start_year} - {exp.is_current ? 'Present' : `${exp.end_month}/${exp.end_year}`}</p>
              {exp.description && <p className="mt-1 text-[15px] text-slate-700 leading-relaxed">{exp.description}</p>}
            </div>
          ))}
        </section>
      )}

      {resume.education?.some((e) => (e.degree_or_cert || '').trim() || (e.institution || '').trim()) && (
        <section className="mb-6">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Education</h4>
          {resume.education.map((edu, idx) => (
            <div key={idx} className="mb-2">
              <p className="font-semibold text-slate-900">{edu.degree_or_cert}</p>
              <p className="text-sm text-slate-500">{edu.institution}{edu.end_year ? ` (${edu.end_year})` : ''}</p>
            </div>
          ))}
        </section>
      )}

      <AwardsDisplay resume={resume} />
    </div>
  );
}

export default SubmittedResumeView;
